/**
 * "Your order moved to Positions" — a small parcel that leaves the ticket and lands on the
 * Positions nav item.
 *
 * It exists to answer a question the ticket can't answer by disappearing: a resting order is
 * still live when you dismiss it, and without something connecting the two the order just
 * vanishes. The parcel makes the handoff legible — this went there, go look there for it.
 *
 * Deliberately DOM-only (no React state, no re-render) so it can run while the ticket is
 * unmounting, and a no-op when the target is off-screen (collapsed rail, mobile drawer shut)
 * or the user asked for reduced motion. Never rejects: the caller closes the ticket either way.
 */

/** Marks the flight's destination. Set on the Positions nav item in both navs. */
export const NAV_POSITIONS_ATTR = "data-nav-positions";

const FLIGHT_MS = 620;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Nothing to watch it — a hidden tab doesn't render frames or dispatch animation events. */
function tabHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Visible in the viewport (a collapsed/hidden nav has a zero-size rect). */
function visibleRect(el: Element | null): DOMRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  if (r.bottom < 0 || r.top > window.innerHeight) return null;
  return r;
}

/**
 * Fly a parcel from `from` to the Positions nav item. Resolves when it lands (or immediately
 * when there's nothing to animate), so callers can `await` it and then close.
 */
export function flyToPositions(from: HTMLElement | null): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  const target = visibleRect(document.querySelector(`[${NAV_POSITIONS_ATTR}]`));
  const start = visibleRect(from);
  if (!target || !start || prefersReducedMotion() || tabHidden()) return Promise.resolve();

  const node = document.createElement("div");
  node.setAttribute("aria-hidden", "true");
  node.textContent = "📦";
  Object.assign(node.style, {
    position: "fixed",
    left: "0",
    top: "0",
    zIndex: "80",
    fontSize: "20px",
    lineHeight: "1",
    pointerEvents: "none",
    willChange: "transform, opacity",
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(node);

  const x0 = start.left + start.width / 2;
  const y0 = start.top + start.height / 2;
  const x1 = target.left + target.width / 2;
  const y1 = target.top + target.height / 2;
  // Bow the path outward so it reads as a throw rather than a slide.
  const midX = (x0 + x1) / 2 - Math.abs(y1 - y0) * 0.18;
  const midY = (y0 + y1) / 2 - 40;

  const anim = node.animate(
    [
      { transform: `translate(${x0}px, ${y0}px) scale(0.6)`, opacity: 0 },
      { transform: `translate(${x0}px, ${y0}px) scale(1)`, opacity: 1, offset: 0.12 },
      { transform: `translate(${midX}px, ${midY}px) scale(1.08)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${x1}px, ${y1}px) scale(0.5)`, opacity: 0 },
    ],
    { duration: FLIGHT_MS, easing: "cubic-bezier(.4,.1,.3,1)" },
  );

  // The caller closes the ticket when this resolves, so resolution must NOT depend on the
  // animation reporting back. A tab hidden mid-flight stops dispatching `finish` entirely
  // (frames are throttled), which would strand the parcel on screen and leave the ticket
  // open forever. The timer is the guarantee; the events just make it prompt.
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      node.remove();
      pulseTarget();
      resolve();
    };
    const timer = setTimeout(done, FLIGHT_MS + 200);
    anim.addEventListener("finish", done, { once: true });
    anim.addEventListener("cancel", done, { once: true });
  });
}

/** A short receipt on the nav item so the landing is noticed. */
function pulseTarget(): void {
  const el = document.querySelector<HTMLElement>(`[${NAV_POSITIONS_ATTR}]`);
  if (!el) return;
  el.classList.remove("nav-landed");
  // Reflow so the class re-triggers when two orders land back to back.
  void el.offsetWidth;
  el.classList.add("nav-landed");
  window.setTimeout(() => el.classList.remove("nav-landed"), 900);
}
